import type { CascadingFieldDefault, JsonFormPropertyV2, JsonFormEnumOptionV2 } from '../FormComponent/types'
import { DataTableV2DataRow } from './types'

/**
 * Type guard to check if choices array contains JsonFormEnumOptionV2 objects (title-value objects)
 * rather than primitive values.
 *
 * @param choices - The choices array to check
 * @returns true if the choices are JsonFormEnumOptionV2 objects, false otherwise
 */
function isTitleValueChoices(
  choices: (string | number)[] | JsonFormEnumOptionV2[] | undefined
): choices is JsonFormEnumOptionV2[] {
  if (!choices || choices.length === 0) return false
  return isTitleValueObject(choices[0])
}

/**
 * Checks if an object has both 'title' and 'value' properties,
 * where 'title' is a string and 'value' is either a string or a number.
 *
 * @param value - The value to check
 * @returns true if the value matches the criteria, false otherwise
 */
export function isTitleValueObject(value: unknown): boolean {
  return (
    typeof value === 'object' &&
    value !== null &&
    'title' in value &&
    'value' in value &&
    typeof (value as { title: string }).title === 'string' &&
    (typeof (value as { value: string }).value === 'string' || typeof (value as { value: number }).value === 'number')
  )
}

export function isProgressStatusObject(value: unknown): boolean {
  return typeof value === 'object' && value !== null && 'color' in value && 'marker' in value && 'tooltip' in value
}

export function isProgressStatusArray(value: unknown): boolean {
  return typeof value === 'object' && value !== null && Array.isArray(value) && value.every(isProgressStatusObject)
}

export function isEmptyObject(value: unknown): value is Record<string, never> {
  return typeof value === 'object' && value !== null && Object.keys(value).length === 0
}

/**
 * Type guard to check if a value conforms to the CascadingFieldDefault interface.
 * All properties are optional, so we check that:
 * - The value is a non-null object
 * - Any present properties have the correct types (string or string[] for level3-5)
 *
 * @param value - The value to check
 * @returns true if the value conforms to CascadingFieldDefault, false otherwise
 */
export function isCascadingFieldValue(value: unknown): value is CascadingFieldDefault {
  if (typeof value !== 'object' || value === null) {
    return false
  }

  const obj = value as Record<string, unknown>

  // Helper to check if a value is a string or undefined
  const isStringOrUndefined = (v: unknown): boolean => v === undefined || typeof v === 'string'

  // Helper to check if a value is a string, string[], or undefined
  const isStringOrStringArrayOrUndefined = (v: unknown): boolean =>
    v === undefined || typeof v === 'string' || (Array.isArray(v) && v.every((item) => typeof item === 'string'))

  // Check level1 and level2 (string | undefined)
  if (!isStringOrUndefined(obj.level1)) return false
  if (!isStringOrUndefined(obj.level2)) return false

  // Check level3, level4, level5 (string | string[] | undefined)
  if (!isStringOrStringArrayOrUndefined(obj.level3)) return false
  if (!isStringOrStringArrayOrUndefined(obj.level4)) return false
  if (!isStringOrStringArrayOrUndefined(obj.level5)) return false

  return true
}

/**
 * Transforms a DataTableV2DataRow by extracting primitive values from TitleValueObjects.
 *
 * This function iterates over all properties of the formData object and:
 * 1. If a value is a TitleValueObject ({ title: string, value: string | number }),
 *    it replaces that value with just the primitive `value` property.
 * 2. If a value is an array of TitleValueObjects, it replaces the array with
 *    an array of primitive values extracted from each object's `value` property.
 * 3. All other values are preserved as-is.
 *
 * @param formData - The DataTableV2DataRow to transform
 * @returns A new DataTableV2DataRow with TitleValueObjects replaced by their primitive values
 */
export function transformTitleValueObjectsToValues(formData: DataTableV2DataRow): DataTableV2DataRow {
  const result: DataTableV2DataRow = { id: formData.id }

  for (const [key, value] of Object.entries(formData)) {
    if (key === 'id') {
      continue // Already copied above
    }

    if (isTitleValueObject(value)) {
      // Case 1: Single TitleValueObject - extract just the value
      result[key] = (value as { title: string; value: string | number }).value
    } else if (Array.isArray(value) && value.length > 0 && isTitleValueObject(value[0])) {
      // Case 2: Array of TitleValueObjects - extract array of values
      // Check the type of the first item's value to determine the array type
      const firstValue = (value[0] as { title: string; value: string | number }).value
      if (typeof firstValue === 'string') {
        result[key] = (value as { title: string; value: string }[]).map((item) => item.value)
      } else {
        result[key] = (value as { title: string; value: number }[]).map((item) => item.value)
      }
    } else {
      // Keep the original value
      result[key] = value
    }
  }

  return result
}

/**
 * Transforms primitive values in a data record back to TitleValueObjects based on form property definitions.
 *
 * This function is the reverse of `transFormTitleValueObjectsToValues`. It iterates over all
 * properties in the data object and:
 * 1. If the matching JsonFormPropertyV2 has a `choices` property that is an array of title-value
 *    objects, it finds the matching choice by value and replaces the primitive with the full
 *    title-value object.
 * 2. If the matching JsonFormPropertyV2 has an `items.choices` property that is an array of
 *    title-value objects, it maps each primitive value in the data array to its corresponding
 *    title-value object.
 * 3. All other values are preserved as-is.
 *
 * @param data - The data record containing primitive values to transform
 * @param formProperties - The form property definitions containing choices with title-value mappings
 * @returns A new record with primitive values replaced by their corresponding title-value objects
 */
export function transformValuesToTitleValueObjects(
  data: Record<string, unknown>,
  formProperties: JsonFormPropertyV2[]
): Record<string, unknown> {
  const result: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(data)) {
    // Find the matching form property by id
    const formProperty = formProperties.find((prop) => prop.id === key)

    if (!formProperty) {
      // No matching property, keep the original value
      result[key] = value
      continue
    }

    // Case 1: Single value with choices that are title-value objects
    if (formProperty.choices && isTitleValueChoices(formProperty.choices)) {
      const matchingChoice = formProperty.choices.find((choice) => choice.value === value)
      if (matchingChoice) {
        result[key] = matchingChoice
      } else {
        result[key] = value
      }
      continue
    }

    // Case 2: Array value with items.choices that are title-value objects
    if (Array.isArray(value) && formProperty.items?.choices && isTitleValueChoices(formProperty.items.choices)) {
      const choices = formProperty.items.choices
      result[key] = value.map((primitiveValue) => {
        const matchingChoice = choices.find((choice) => choice.value === primitiveValue)
        return matchingChoice ?? primitiveValue
      })
      continue
    }

    // Default: keep the original value
    result[key] = value
  }

  return result
}
